// src/components/padrekko/PadLayer.jsx
//
// Renders pad polygons from the /vtiles/pad endpoint.
//
// Three visual states, three layers stacked in order:
//   1. pads-fill       — base translucent fill, always on
//   2. pads-outline    — crisp outline, always on
//   3. pads-selected   — thick highlight for the selected pad (filter-driven)
//
// Click → usePadStore.selectPad with geometry pulled from the tile feature.
// Hover → usePadStore.setHoveredPad for cursor + subtle highlight.

import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { usePadStore } from "../../stores/usePadStore";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { useLayersStore } from "../../stores/useLayersStore";
import { PAD_LAYERS } from "../../constants/mapLayerIds";
import { useFeatureColorStore } from "../../stores/useFeatureColorStore";
import { effectiveFeatureColor } from "../../constants/featureColors";

const SOURCE_ID = "pads";
// asset_type in aiprime.vector_assets_org is 'well_pad'
const VTILE_BASE = `${window.location.origin}/vtiles/well_pad/{z}/{x}/{y}.pbf`;
function tileUrl(aoiId) {
  return aoiId ? `${VTILE_BASE}?aoi=${aoiId}` : VTILE_BASE;
}
// Layer ids live in constants/mapLayerIds.js (shared with MapView's
// click-priority list). Aliased back to LAYERS so call sites are unchanged.
const LAYERS = PAD_LAYERS;

export default function PadLayer({ map, addLayer }) {
  // WHY PadLayer READS THE OVERRIDE STORE AT ALL.
  //
  // Pads are the one catalog row this component renders rather than the generic
  // feature renderer, so its swatch was a read-only legend chip -- the picker
  // opened, wrote an override, and nothing on the map changed. Reading the same
  // store the swatch writes is what closes that loop.
  //
  // Only the BASE fill and outline follow it. The hover and selection paints
  // stay fixed: those are interaction states, not the pad's colour, and tying
  // them to a user-chosen hue is how a selection stops being legible against
  // the thing it selects.
  const padOverrides = useFeatureColorStore((s) => s.overrides);
  const padColor = effectiveFeatureColor("well_pad", padOverrides);

  const hoveredPadId = usePadStore(s => s.hoveredPadId);
  const selectedPad  = usePadStore(s => s.selectedPad);
  const selectPad    = usePadStore(s => s.selectPad);
  const clearSel     = usePadStore(s => s.clearSelection);
  const setHovered   = usePadStore(s => s.setHoveredPad);

  const activeOperatingAoiId = useOperatingAoiContext(s => s.activeOperatingAoiId);

  // Features-tab visibility wiring — mirrors the wells precedent in
  // MapView.jsx. `?? true` fallbacks defend against the brief window
  // after store version migration where assets.pads might not exist yet.
  const padsEnabled  = useLayersStore(s => s.assets.pads?.enabled ?? true);
  const padsPolygonOn = useLayersStore(s => s.assets.pads?.geometry?.polygon ?? true);
  const visible = padsEnabled && padsPolygonOn;

  const initRef = useRef(false);

  /* ------------------------------------------------------------------ */
  /* REGISTER SOURCE + LAYERS (once)                                    */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!map || initRef.current) return;

    const setup = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: "vector",
          tiles: [tileUrl(activeOperatingAoiId)],
          minzoom: 10,
          maxzoom: 18,
        });
      }

      // Base translucent fill
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.FILL,
          type: "fill",
          source: SOURCE_ID,
          "source-layer": "assets",
          minzoom: 10,
          layout: { visibility: "visible" },
          paint: {
            // Initial value only. The live colour is applied by the effect at
            // the bottom of this component, which is what makes the FEATURES
            // swatch for well_pad actually do something.
            "fill-color":   padColor,
            "fill-opacity": [
              "interpolate", ["linear"], ["zoom"],
              10, 0.04,
              14, 0.08,
              17, 0.12,
            ],
          },
        },
      });

      // Outline
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.OUTLINE,
          type: "line",
          source: SOURCE_ID,
          "source-layer": "assets",
          minzoom: 10,
          layout: { visibility: "visible" },
          paint: {
            "line-color":   padColor,
            "line-width": [
              "interpolate", ["linear"], ["zoom"],
              10, 0.8,
              14, 1.4,
              17, 2.0,
            ],
            "line-opacity": 0.85,
          },
        },
      });

      // Hover highlight — filter-driven. Outline-only via fill-outline-color
      // in the warm-orange selected accent so hover reads as a peek at the
      // selection treatment. fill-color is preserved at 0 opacity for an
      // easy revert to a fill-based hover if the visual call changes.
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.HOVER,
          type: "fill",
          source: SOURCE_ID,
          "source-layer": "assets",
          minzoom: 10,
          layout: { visibility: "visible" },
          paint: {
            "fill-color":         "#16f2d4",
            "fill-opacity":       0,
            "fill-outline-color": "#fda261",
          },
          filter: ["==", ["get", "id"], ""],
        },
      });

      // Selected highlight — thicker outline + warmer fill
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.SELECTED,
          type: "line",
          source: SOURCE_ID,
          "source-layer": "assets",
          minzoom: 10,
          layout: { visibility: "visible" },
          paint: {
            "line-color": "#fda261",   // warm accent — matches lateral orange
            "line-width": 3.5,
            "line-opacity": 1.0,
          },
          filter: ["==", ["get", "id"], ""],
        },
      });

      initRef.current = true;
    };

    if (map.isStyleLoaded()) setup();
    else map.once("idle", setup);

    return () => {
      if (!map?.getStyle?.()) { initRef.current = false; return; }
      Object.values(LAYERS).forEach((id) => {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
      });
      try { if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID); } catch (_) {}
      initRef.current = false;
    };
  }, [map, addLayer]);

  /* ------------------------------------------------------------------ */
  /* AOI CHANGE — update tile source URL                                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!map) return;
    const apply = () => {
      const src = map.getSource(SOURCE_ID);
      if (src?.setTiles) src.setTiles([tileUrl(activeOperatingAoiId)]);
    };
    if (map.isStyleLoaded()) apply();
    else map.once("idle", apply);
  }, [map, activeOperatingAoiId]);

  /* ------------------------------------------------------------------ */
  /* FEATURES VISIBILITY                                                */
  /* ------------------------------------------------------------------ */
  //
  // Drives the Features-tab toggle (useLayersStore.assets.pads). The
  // main effect runs on state changes; the styledata listener re-applies
  // after any style reload (base-map swap, etc.) so a previously-hidden
  // pad layer doesn't ghost back to visible on the next render frame.
  //
  // Click/hover handlers stay attached regardless of visibility — that's
  // the wells precedent. MapLibre's events fire on whatever layer is
  // rendered; hidden layers don't dispatch clicks, so toggling off
  // naturally turns off the interaction without us managing it.

  useEffect(() => {
    if (!map) return;
    const apply = () => {
      const vis = visible ? "visible" : "none";
      Object.values(LAYERS).forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once("idle", apply);
  }, [map, visible]);

  /* ──── live pad colour ──── */
  //
  // The layer-add effect runs once, so the colour baked in there is only the
  // value at mount. This repaints when the user picks a new one.
  //
  // Guarded on getLayer rather than on a ready flag: the layers may not exist
  // yet (pads off, or style still loading), and setPaintProperty on a missing
  // layer throws. When they appear they are created with the current padColor
  // anyway, so there is nothing to catch up.
  useEffect(() => {
    if (!map?.getStyle?.()) return;
    if (map.getLayer(LAYERS.FILL)) map.setPaintProperty(LAYERS.FILL, "fill-color", padColor);
    if (map.getLayer(LAYERS.OUTLINE)) map.setPaintProperty(LAYERS.OUTLINE, "line-color", padColor);
  }, [map, padColor]);

  useEffect(() => {
    if (!map) return;
    const onStyleData = () => {
      const vis = visible ? "visible" : "none";
      Object.values(LAYERS).forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
      });
    };
    map.on("styledata", onStyleData);
    return () => map.off("styledata", onStyleData);
  }, [map, visible]);

  /* ------------------------------------------------------------------ */
  /* HOVER FILTER                                                       */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!map || !map.getLayer(LAYERS.HOVER)) return;
    map.setFilter(LAYERS.HOVER, ["==", ["get", "id"], hoveredPadId ?? ""]);
  }, [map, hoveredPadId]);

  /* ------------------------------------------------------------------ */
  /* SELECTED FILTER                                                    */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!map || !map.getLayer(LAYERS.SELECTED)) return;
    map.setFilter(
      LAYERS.SELECTED,
      ["==", ["get", "id"], selectedPad?.id ?? ""]
    );
  }, [map, selectedPad?.id]);

  /* ------------------------------------------------------------------ */
  /* FIT TO SELECTED PAD                                                */
  /* ------------------------------------------------------------------ */
  //
  // Fit the viewport to the selected pad's geometry. Walks all coords
  // (handles Polygon + MultiPolygon) and extends a LngLatBounds.
  //
  // maxZoom: 17 keeps small pads from zooming past comfort. Padding
  // leaves room for the panel overlay on the left.

  useEffect(() => {
    if (!map || !selectedPad?.geom) return;

    const geom = selectedPad.geom;
    const rings =
      geom.type === "Polygon"      ? geom.coordinates :
      geom.type === "MultiPolygon" ? geom.coordinates.flat() :
      null;
    if (!rings?.length) return;

    const first = rings[0][0];
    const bounds = new maplibregl.LngLatBounds(first, first);
    for (const ring of rings) {
      for (const coord of ring) bounds.extend(coord);
    }

    map.fitBounds(bounds, {
      padding: { top: 80, bottom: 80, left: 80, right: 80 },
      maxZoom: 17,
      duration: 600,
    });
  }, [map, selectedPad?.id]);

  /* ------------------------------------------------------------------ */
  /* INTERACTION                                                        */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!map) return;

    const handleClick = (e) => {
      // MlDraw owns clicks while drawing — don't swap selection underneath
      if (usePadStore.getState().drawingRoad) return;

      const f = e.features?.[0];
      if (!f) return;

      const id = f.properties?.id;
      if (!id) return;

      // No-op if the user clicks the already-selected pad. selectPad would
      // re-fetch access roads from the server and wipe any user-drawn roads
      // they've added in this session.
      if (usePadStore.getState().selectedPad?.id === id) return;

      // Vector tile features carry their geometry in e.features[].geometry,
      // already in GeoJSON form after MapLibre's parse. That's what turf needs.
      selectPad({
        id,
        properties: f.properties,
        geom: f.geometry,
      });
    };

    const handleEnter = (e) => {
      if (usePadStore.getState().drawingRoad) return;
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (f?.properties?.id) setHovered(f.properties.id);
    };

    const handleLeave = () => {
      if (usePadStore.getState().drawingRoad) return;
      map.getCanvas().style.cursor = "";
      setHovered(null);
    };

    // Clicks on empty map space clear selection — matches SiteID behavior.
    // Guards:
    //   - If a road was clicked (hitbox is under the cursor), bail.
    //     This supersedes the _padRoadHandled event flag, which is
    //     unreliable across MapLibre versions (layer handler order).
    //   - If the user is drawing a road, map clicks belong to MlDraw — bail.
    const handleEmptyClick = (e) => {
      if (usePadStore.getState().drawingRoad) return;

      const hits = map.queryRenderedFeatures(e.point);
      const padHit  = hits.some(h => h.layer?.id === LAYERS.FILL);
      const roadHit = hits.some(h => h.layer?.id === "pad-access-roads-hitbox");

      if (padHit)  return;   // pad click handled elsewhere
      if (roadHit) return;   // road click handled elsewhere

      clearSel();
    };

    map.on("click",      LAYERS.FILL, handleClick);
    map.on("mouseenter", LAYERS.FILL, handleEnter);
    map.on("mousemove",  LAYERS.FILL, handleEnter);
    map.on("mouseleave", LAYERS.FILL, handleLeave);
    map.on("click", handleEmptyClick);

    return () => {
      map.off("click",      LAYERS.FILL, handleClick);
      map.off("mouseenter", LAYERS.FILL, handleEnter);
      map.off("mousemove",  LAYERS.FILL, handleEnter);
      map.off("mouseleave", LAYERS.FILL, handleLeave);
      map.off("click", handleEmptyClick);
    };
  }, [map, selectPad, clearSel, setHovered]);

  return null;
}
